/**
 * 計助ダッシュボード: t検定用 2列データ入力フォーム
 * - 1行目: 項目ラベル（群A名・群B名）
 * - 2行目以降: 各群の値を縦に入力。行数は「各群のデータ数」（対応ありはペア数）に一致。
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tTestTwoColumnFormToGridData,
  tTestTwoColumnFormToGridDataPaired,
  gridDataToTTestTwoColumnForm,
  gridDataToTTestTwoColumnFormPaired,
  type TTestTwoColumnForm,
  normalizeKeisukeNumericInput,
} from '../../../../utils/keisukeInputFormUtils';
import { resolveUnpairedLongFormatColumnKeys } from '../../../../utils/keisukeStockColumnLabels';
import type { DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  KEISUKE_GROUP_OBSERVATION_N_MAX,
  ttestNonparametricInferential,
  useKeisukeDashboardStore,
} from '../../../../stores/systems/keisuke/dashboardStore';
import { useShallow } from 'zustand/react/shallow';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import { useDebouncedFormApply } from './useDebouncedFormApply';
import styles from './KeisukeDashboardCenterPane.module.scss';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';
import KeisukeDashboardGridObservationCountRow from './KeisukeDashboardGridObservationCountRow';
import { KeisukeLabeledBinaryRadioGroup } from './keisukeDashboardBinaryRadioGroup';
import { tTestParametricInferenceLabel } from '../../../../utils/keisukeTTestDisplay';
import { KEISUKE_DASHBOARD_NEW_EVENT } from './KeisukeDashboardBarChartSettingsContext';
import {
  clampKeisukeDashboardDataColWidthPx,
  KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX,
  keisukeDashboardFillDataColWidthPx,
} from './keisukeDashboardDataGridLayout';

const FALLBACK_LABEL_A = 'Group A';
const FALLBACK_LABEL_B = 'Group B';
const FALLBACK_LABEL_BEFORE = 'Before';
const FALLBACK_LABEL_AFTER = 'After';

/** form→store 適用後、grid→form の巻き戻しを抑える窓（debounce 280ms + slack） */
const TTEST_GRID_SYNC_SUPPRESS_MS = 950;

/** モード切替で置き換えてよい「未カスタム」ラベル（多言語の既定値含む） */
function isStockTTestColumnLabel(
  label: string,
  stock: ReadonlySet<string>
): boolean {
  const t = label.trim();
  return !t || stock.has(t);
}

function buildTTestStockLabelSet(
  unpairedA: string,
  unpairedB: string,
  pairedA: string,
  pairedB: string
): Set<string> {
  return new Set(
    [
      unpairedA,
      unpairedB,
      pairedA,
      pairedB,
      FALLBACK_LABEL_A,
      FALLBACK_LABEL_B,
      FALLBACK_LABEL_BEFORE,
      FALLBACK_LABEL_AFTER,
      '群A',
      '群B',
      '前',
      '後',
      'Group',
      'Value',
      '群',
      '値',
    ].map((s) => s.trim())
  );
}

/** サンプルの Before/After 等を UI 言語の既定ラベルへ寄せる */
function localizeStockTTestFormLabels(
  form: TTestTwoColumnForm,
  stock: ReadonlySet<string>,
  defaultA: string,
  defaultB: string
): TTestTwoColumnForm {
  return {
    ...form,
    labelColA: isStockTTestColumnLabel(form.labelColA, stock) ? defaultA : form.labelColA,
    labelColB: isStockTTestColumnLabel(form.labelColB, stock) ? defaultB : form.labelColB,
  };
}

function resizeTTestValueRows(rows: [string, string][], n: number): [string, string][] {
  const next = rows.slice(0, n);
  while (next.length < n) next.push(['', ''] as [string, string]);
  return next;
}

/** ストア→フォーム同期: 途中入力（1群だけ）でラベル・行数を潰さない */
function mergeGridIntoTTestForm(
  fromGrid: TTestTwoColumnForm,
  prev: TTestTwoColumnForm
): TTestTwoColumnForm {
  const rowCount = Math.max(fromGrid.valueRows.length, prev.valueRows.length, 1);
  return {
    labelColA: fromGrid.labelColA.trim() ? fromGrid.labelColA : prev.labelColA,
    labelColB: fromGrid.labelColB.trim() ? fromGrid.labelColB : prev.labelColB,
    valueRows: resizeTTestValueRows(fromGrid.valueRows, rowCount),
  };
}

function tTestTwoColumnFormEqual(a: TTestTwoColumnForm, b: TTestTwoColumnForm): boolean {
  if (a.labelColA !== b.labelColA || a.labelColB !== b.labelColB) return false;
  if (a.valueRows.length !== b.valueRows.length) return false;
  for (let i = 0; i < a.valueRows.length; i++) {
    if (String(a.valueRows[i][0] ?? '') !== String(b.valueRows[i][0] ?? '')) return false;
    if (String(a.valueRows[i][1] ?? '') !== String(b.valueRows[i][1] ?? '')) return false;
  }
  return true;
}

function mergeTTestLabelPaste(prev: TTestTwoColumnForm, anchorCol: 0 | 1, matrix: string[][]): TTestTwoColumnForm {
  const line = matrix[0] ?? [];
  if (anchorCol === 0) {
    return {
      ...prev,
      labelColA: line[0] ?? prev.labelColA,
      labelColB: line.length > 1 ? (line[1] ?? prev.labelColB) : prev.labelColB,
    };
  }
  return {
    ...prev,
    labelColB: line[0] ?? prev.labelColB,
  };
}

function mergeTTestValuePaste(
  prev: TTestTwoColumnForm,
  startRow: number,
  startCol: 0 | 1,
  matrix: string[][],
  minPadRows: number
): TTestTwoColumnForm {
  const valueRows = prev.valueRows.map((r) => [...r] as [string, string]);
  const ensureRow = (idx: number) => {
    while (valueRows.length <= idx) valueRows.push(['', ''] as [string, string]);
  };
  for (let i = 0; i < matrix.length; i++) {
    const r = startRow + i;
    ensureRow(r);
    const line = matrix[i];
    for (let j = 0; j < line.length; j++) {
      const tc = startCol + j;
      if (tc <= 1) valueRows[r][tc] = normalizeKeisukeNumericInput(String(line[j] ?? ''));
    }
  }
  while (valueRows.length < minPadRows) valueRows.push(['', ''] as [string, string]);
  return { ...prev, valueRows };
}

function mergeTTestPasteFromLabelRow(
  prev: TTestTwoColumnForm,
  anchorCol: 0 | 1,
  matrix: string[][],
  nPerGroup: number
): TTestTwoColumnForm {
  if (matrix.length === 0) return prev;
  const withLabels = mergeTTestLabelPaste(prev, anchorCol, matrix);
  const rest = matrix.slice(1);
  if (rest.length === 0) return withLabels;
  const minPad = Math.max(nPerGroup, rest.length);
  return mergeTTestValuePaste(withLabels, 0, anchorCol, rest, minPad);
}

/** 行 -1 = ヘッダー（群ラベル）、0 以上 = データ行 */
type TTestGridCell = { row: number; col: 0 | 1 };
type TTestGridSelection = { a: TTestGridCell; b: TTestGridCell };

function normalizeTTestGridSelection(s: TTestGridSelection): {
  minRow: number;
  maxRow: number;
  minCol: 0 | 1;
  maxCol: 0 | 1;
} {
  const minRow = Math.min(s.a.row, s.b.row);
  const maxRow = Math.max(s.a.row, s.b.row);
  const minCol = Math.min(s.a.col, s.b.col) as 0 | 1;
  const maxCol = Math.max(s.a.col, s.b.col) as 0 | 1;
  return { minRow, maxRow, minCol, maxCol };
}

function tTestGridSelectionCellCount(n: ReturnType<typeof normalizeTTestGridSelection>): number {
  return (n.maxRow - n.minRow + 1) * (n.maxCol - n.minCol + 1);
}

function tTestCellInSelection(row: number, col: 0 | 1, sel: TTestGridSelection | null): boolean {
  if (!sel) return false;
  const n = normalizeTTestGridSelection(sel);
  return row >= n.minRow && row <= n.maxRow && col >= n.minCol && col <= n.maxCol;
}

function tTestCellFromEventTarget(target: EventTarget | null): TTestGridCell | null {
  const el = target as HTMLElement | null;
  if (!el?.closest) return null;
  if (el.closest('[data-tt-ignore-selection="1"]')) return null;
  const inp = el.closest('input[data-tt-row]') as HTMLInputElement | null;
  if (!inp) return null;
  const rs = inp.getAttribute('data-tt-row');
  const cs = inp.getAttribute('data-tt-col');
  if (rs == null || cs == null) return null;
  const row = parseInt(rs, 10);
  const col = parseInt(cs, 10);
  if (col !== 0 && col !== 1) return null;
  if (Number.isNaN(row) || row < -1) return null;
  return { row, col };
}

/** copy/paste の target が input にならない環境向けに activeElement でフォーカスセルを解決 */
function tTestInputFromClipboardEvent(
  e: React.SyntheticEvent,
  tableEl: HTMLTableElement | null
): HTMLInputElement | null {
  const t = e.target;
  if (t instanceof HTMLInputElement && tableEl?.contains(t)) return t;
  const ae = document.activeElement;
  if (ae instanceof HTMLInputElement && tableEl?.contains(ae)) return ae;
  return null;
}

function buildTsvFromTTestSelection(
  f: TTestTwoColumnForm,
  n: ReturnType<typeof normalizeTTestGridSelection>
): string {
  const lines: string[][] = [];
  for (let r = n.minRow; r <= n.maxRow; r++) {
    const line: string[] = [];
    for (let c = n.minCol; c <= n.maxCol; c++) {
      if (r === -1) {
        line.push(c === 0 ? f.labelColA : f.labelColB);
      } else {
        line.push(String(f.valueRows[r]?.[c] ?? ''));
      }
    }
    lines.push(line);
  }
  return lines.map((ln) => ln.join('\t')).join('\n');
}

/** 選択範囲のセルを空文字にする（範囲 Delete / Cut 用） */
function clearTTestSelectionCells(
  f: TTestTwoColumnForm,
  n: ReturnType<typeof normalizeTTestGridSelection>
): TTestTwoColumnForm {
  let labelColA = f.labelColA;
  let labelColB = f.labelColB;
  const valueRows = f.valueRows.map((row) => [row[0], row[1]] as [string, string]);
  for (let r = n.minRow; r <= n.maxRow; r++) {
    for (let c = n.minCol; c <= n.maxCol; c++) {
      if (r === -1) {
        if (c === 0) labelColA = '';
        else labelColB = '';
      } else if (valueRows[r]) {
        valueRows[r]![c] = '';
      }
    }
  }
  return { labelColA, labelColB, valueRows };
}

/** copy/cut 共通: 有効選択と「入力内部分選択ならネイティブに任せる」判定 */
function resolveTTestClipboardSelection(
  gridSelection: TTestGridSelection | null,
  inp: HTMLInputElement
): { norm: ReturnType<typeof normalizeTTestGridSelection>; deferToNative: boolean } | null {
  const focusedCell = tTestCellFromEventTarget(inp);
  const effectiveSel =
    gridSelection ??
    (focusedCell ? ({ a: focusedCell, b: focusedCell } satisfies TTestGridSelection) : null);
  if (!effectiveSel) return null;
  const norm = normalizeTTestGridSelection(effectiveSel);
  const area = tTestGridSelectionCellCount(norm);
  const deferToNative =
    area <= 1 &&
    inp.selectionStart != null &&
    inp.selectionEnd != null &&
    inp.selectionStart !== inp.selectionEnd;
  return { norm, deferToNative };
}

/** キャレットがセル端にあり、矢印で隣セルへ移してよいか */
function tTestArrowShouldLeaveCell(
  key: string,
  inp: HTMLInputElement
): boolean {
  if (key === 'ArrowUp' || key === 'ArrowDown') return true;
  const start = inp.selectionStart ?? 0;
  const end = inp.selectionEnd ?? 0;
  if (start !== end) return false;
  const len = inp.value.length;
  if (key === 'ArrowLeft') return start === 0;
  if (key === 'ArrowRight') return end === len;
  return false;
}

function getTTestPasteAnchorCell(sel: TTestGridSelection | null, focus: TTestGridCell): TTestGridCell {
  if (!sel) return focus;
  const n = normalizeTTestGridSelection(sel);
  if (tTestGridSelectionCellCount(n) <= 1) return focus;
  return { row: n.minRow, col: n.minCol };
}

function applyTTestMatrixPaste(
  prev: TTestTwoColumnForm,
  matrix: string[][],
  anchor: TTestGridCell,
  nPerGroup: number
): TTestTwoColumnForm {
  if (matrix.length === 0) return prev;
  if (anchor.row === -1) {
    return mergeTTestPasteFromLabelRow(prev, anchor.col, matrix, nPerGroup);
  }
  const minPad = Math.max(nPerGroup, anchor.row + matrix.length);
  return mergeTTestValuePaste(prev, anchor.row, anchor.col, matrix, minPad);
}

export interface KeisukeDashboardDataFormTTestTwoColumnsProps {
  gridData: DataRow[];
  gridHeaders: string[];
  groupColumn?: string;
  valueColumn?: string;
  /** 測定の表示名（例: Weight）。長形式 value 列の見出しに使う */
  valueAxisLabel?: string;
  /** 対応ありのときは 1行1対（Before/After）形式で渡す */
  testType?: 'unpaired' | 'paired';
  beforeColumn?: string;
  afterColumn?: string;
  onApply: (data: DataRow[], headers: string[], opts?: { paired?: boolean }) => void;
  disabled?: boolean;
  /** 長形式2列と同じデータ列幅（px）。中央ペインの `longFormatTwoColWidthPx` と同期 */
  linkedDataColumnWidthPx?: number;
  /** 2列フォームで列幅を変えたときに長形式側へ反映（ポインタアップ時） */
  onLinkedDataColumnWidthCommit?: (px: number) => void;
  /** 各群（対応ありのときはペア）あたりのデータ行数。ストアの `tTestNPerGroup` と同期 */
  nPerGroup: number;
  onNPerGroupChange: (n: number) => void;
}

export default function KeisukeDashboardDataFormTTestTwoColumns({
  gridData,
  gridHeaders,
  groupColumn,
  valueColumn,
  valueAxisLabel,
  testType = 'unpaired',
  beforeColumn,
  afterColumn,
  onApply,
  disabled = false,
  linkedDataColumnWidthPx,
  onLinkedDataColumnWidthCommit,
  nPerGroup,
  onNPerGroupChange,
}: KeisukeDashboardDataFormTTestTwoColumnsProps) {
  const { t } = useTranslation();
  const { setAnalysisOptions, ttestInferential, equalVariance } = useKeisukeDashboardStore(
    useShallow((s) => ({
      setAnalysisOptions: s.setAnalysisOptions,
      ttestInferential: s.analysisOptions.ttestInferential ?? 'parametric',
      equalVariance: s.analysisOptions.equalVariance === true,
    }))
  );
  const isPaired = testType === 'paired';
  const isParametric = ttestInferential === 'parametric';
  const setTestType = useCallback(
    (next: 'unpaired' | 'paired') => {
      if (next === testType) return;
      setAnalysisOptions({ testType: next }, { runAnalysis: false });
    },
    [testType, setAnalysisOptions]
  );
  const setInferential = useCallback(
    (next: 'parametric' | 'nonparametric') => {
      if (next === 'parametric') {
        if (isParametric) return;
        setAnalysisOptions({ ttestInferential: 'parametric' });
        return;
      }
      const np = ttestNonparametricInferential(testType);
      if (ttestInferential === np) return;
      setAnalysisOptions({ ttestInferential: np });
    },
    [isParametric, ttestInferential, testType, setAnalysisOptions]
  );
  const setEqualVariance = useCallback(
    (next: boolean) => {
      if (next === equalVariance) return;
      setAnalysisOptions({ equalVariance: next });
    },
    [equalVariance, setAnalysisOptions]
  );
  const unpairedDefaultA = t('keisuke.dashboard.inputForm.defaultGroupA', {
    defaultValue: FALLBACK_LABEL_A,
  });
  const unpairedDefaultB = t('keisuke.dashboard.inputForm.defaultGroupB', {
    defaultValue: FALLBACK_LABEL_B,
  });
  const pairedDefaultA = t('keisuke.dashboard.inputForm.defaultBefore', {
    defaultValue: FALLBACK_LABEL_BEFORE,
  });
  const pairedDefaultB = t('keisuke.dashboard.inputForm.defaultAfter', {
    defaultValue: FALLBACK_LABEL_AFTER,
  });
  const defaultLabelA = isPaired ? pairedDefaultA : unpairedDefaultA;
  const defaultLabelB = isPaired ? pairedDefaultB : unpairedDefaultB;
  const labelAriaA = t(
    isPaired
      ? 'keisuke.dashboard.inputForm.labelColumnBefore'
      : 'keisuke.dashboard.inputForm.labelColumnA'
  );
  const labelAriaB = t(
    isPaired
      ? 'keisuke.dashboard.inputForm.labelColumnAfter'
      : 'keisuke.dashboard.inputForm.labelColumnB'
  );
  const resizeColumnsAria = t(
    isPaired
      ? 'keisuke.dashboard.inputForm.resizeColumnsBetweenBeforeAfter'
      : 'keisuke.dashboard.inputForm.resizeColumnsBetweenAB'
  );
  const fieldIdPrefix = useId();
  const skipSyncFromGridRef = useRef(false);
  const suppressGridSyncUntilRef = useRef(0);
  const tTestColDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  /** fit-content の tableScroll ではなく、親 form 幅で自動拡幅する */
  const formWrapRef = useRef<HTMLDivElement>(null);
  const gridAnchorRef = useRef<TTestGridCell | null>(null);
  const [gridSelection, setGridSelection] = useState<TTestGridSelection | null>(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [userResizedColWidth, setUserResizedColWidth] = useState(false);
  const [viewportInnerW, setViewportInnerW] = useState(0);
  const [tTestDataColWidthPx, setTTestDataColWidthPx] = useState(() =>
    clampKeisukeDashboardDataColWidthPx(
      linkedDataColumnWidthPx ?? KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX
    )
  );
  const [form, setForm] = useState<TTestTwoColumnForm>(() => {
    const empty = {
      labelColA: defaultLabelA,
      labelColB: defaultLabelB,
      valueRows: Array.from({ length: Math.max(1, nPerGroup) }, () => ['', ''] as [string, string]),
    };
    const fromGrid =
      testType === 'paired'
        ? gridDataToTTestTwoColumnFormPaired(gridData, gridHeaders, beforeColumn, afterColumn)
        : gridDataToTTestTwoColumnForm(gridData, gridHeaders, groupColumn, valueColumn);
    if (!fromGrid) return empty;
    const stock = buildTTestStockLabelSet(
      unpairedDefaultA,
      unpairedDefaultB,
      pairedDefaultA,
      pairedDefaultB
    );
    return localizeStockTTestFormLabels(
      mergeGridIntoTTestForm(fromGrid, empty),
      stock,
      defaultLabelA,
      defaultLabelB
    );
  });

  const prevNPerGroupRef = useRef(nPerGroup);
  useEffect(() => {
    if (prevNPerGroupRef.current === nPerGroup) return;
    prevNPerGroupRef.current = nPerGroup;
    setForm((prev) => ({ ...prev, valueRows: resizeTTestValueRows(prev.valueRows, nPerGroup) }));
  }, [nPerGroup]);

  /** 対応なし⇔あり: 既定ラベルを同期しストアへ反映（paint 前・grid 同期より先） */
  const prevTestTypeRef = useRef(testType);
  const stockLabels = useMemo(
    () =>
      buildTTestStockLabelSet(
        unpairedDefaultA,
        unpairedDefaultB,
        pairedDefaultA,
        pairedDefaultB
      ),
    [unpairedDefaultA, unpairedDefaultB, pairedDefaultA, pairedDefaultB]
  );
  useLayoutEffect(() => {
    if (prevTestTypeRef.current === testType) return;
    prevTestTypeRef.current = testType;
    const paired = testType === 'paired';
    /**
     * 対応なし⇔ありは列の意味が変わる（独立2群 ≠ 前後ペア）。
     * WT/KO サンプルのまま「前後」と再解析すると誤った対応あり検定になるため、
     * 空フォーム＋モード既定ラベルへ戻す（出典サンプルは CenterPane がクリア）。
     */
    const next: TTestTwoColumnForm = {
      labelColA: paired ? pairedDefaultA : unpairedDefaultA,
      labelColB: paired ? pairedDefaultB : unpairedDefaultB,
      valueRows: Array.from(
        { length: Math.max(1, nPerGroup) },
        () => ['', ''] as [string, string]
      ),
    };
    setForm(next);
    const { headers, data } = paired
      ? tTestTwoColumnFormToGridDataPaired(next)
      : tTestTwoColumnFormToGridData(
          next,
          resolveUnpairedLongFormatColumnKeys(t, {
            groupKey: groupColumn,
            valueKey: valueColumn,
            valueAxisLabel,
          })
        );
    suppressGridSyncUntilRef.current = Date.now() + TTEST_GRID_SYNC_SUPPRESS_MS;
    skipSyncFromGridRef.current = true;
    onApply(data, headers, { paired });
  }, [
    testType,
    unpairedDefaultA,
    unpairedDefaultB,
    pairedDefaultA,
    pairedDefaultB,
    nPerGroup,
    onApply,
    t,
    groupColumn,
    valueColumn,
    valueAxisLabel,
  ]);

  const emptyTTestForm = useCallback(
    (): TTestTwoColumnForm => ({
      labelColA: defaultLabelA,
      labelColB: defaultLabelB,
      valueRows: Array.from({ length: Math.max(1, nPerGroup) }, () => ['', ''] as [string, string]),
    }),
    [defaultLabelA, defaultLabelB, nPerGroup]
  );

  /** 抑止窓中に外部更新（貼付・サンプル）が来ても、窓終了後に取りこぼさない */
  const [gridSyncKick, setGridSyncKick] = useState(0);
  useEffect(() => {
    const wait = suppressGridSyncUntilRef.current - Date.now();
    if (wait > 0) {
      const id = window.setTimeout(() => {
        skipSyncFromGridRef.current = false;
        setGridSyncKick((k) => k + 1);
      }, wait + 1);
      return () => window.clearTimeout(id);
    }
    if (skipSyncFromGridRef.current) {
      skipSyncFromGridRef.current = false;
      return;
    }
    if (testType === 'paired') {
      const fromGrid = gridDataToTTestTwoColumnFormPaired(gridData, gridHeaders, beforeColumn, afterColumn);
      if (fromGrid) {
        setForm((prev) => {
          const merged = mergeGridIntoTTestForm(fromGrid, prev);
          const next = localizeStockTTestFormLabels(
            merged,
            stockLabels,
            pairedDefaultA,
            pairedDefaultB
          );
          return tTestTwoColumnFormEqual(next, prev) ? prev : next;
        });
        // 途中入力で行数が減ってもスピナー n は戻さない（増えたときだけ追従）
        if (fromGrid.valueRows.length > nPerGroup) {
          onNPerGroupChange(fromGrid.valueRows.length);
        }
        return;
      }
    } else {
      const fromGrid = gridDataToTTestTwoColumnForm(gridData, gridHeaders, groupColumn, valueColumn);
      if (fromGrid) {
        setForm((prev) => {
          const merged = mergeGridIntoTTestForm(fromGrid, prev);
          const next = localizeStockTTestFormLabels(
            merged,
            stockLabels,
            unpairedDefaultA,
            unpairedDefaultB
          );
          return tTestTwoColumnFormEqual(next, prev) ? prev : next;
        });
        if (fromGrid.valueRows.length > nPerGroup) {
          onNPerGroupChange(fromGrid.valueRows.length);
        }
        return;
      }
    }
    /** 新規などで headers が空のとき fromGrid は null → ローカル表を残さない */
    if (gridHeaders.length === 0) {
      setForm((prev) => {
        const empty = emptyTTestForm();
        return tTestTwoColumnFormEqual(empty, prev) ? prev : empty;
      });
    }
    // nPerGroup は依存に含めない（スピナーで n だけ変えたときに、未更新の長形式から n を戻さない）
  }, [
    gridData,
    gridHeaders,
    groupColumn,
    valueColumn,
    testType,
    beforeColumn,
    afterColumn,
    onNPerGroupChange,
    emptyTTestForm,
    stockLabels,
    pairedDefaultA,
    pairedDefaultB,
    unpairedDefaultA,
    unpairedDefaultB,
    gridSyncKick,
  ]);

  const emptyAutofocusDoneRef = useRef(false);

  const focusFirstValueCell = useCallback(() => {
    window.requestAnimationFrame(() => {
      formWrapRef.current
        ?.querySelector<HTMLInputElement>('input[data-tt-row="0"][data-tt-col="0"]')
        ?.focus();
    });
  }, []);

  /** 新規／空の初回表示: 先頭セルへフォーカス（1回だけ） */
  useEffect(() => {
    const onNew = () => {
      emptyAutofocusDoneRef.current = false;
      setForm(emptyTTestForm());
      window.requestAnimationFrame(() => {
        emptyAutofocusDoneRef.current = true;
        focusFirstValueCell();
      });
    };
    window.addEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onNew);

    if (!disabled && !emptyAutofocusDoneRef.current) {
      const storeHasRows = gridHeaders.length > 0 && gridData.length > 0;
      if (!storeHasRows) {
        emptyAutofocusDoneRef.current = true;
        focusFirstValueCell();
      }
    }

    return () => window.removeEventListener(KEISUKE_DASHBOARD_NEW_EVENT, onNew);
  }, [disabled, emptyTTestForm, focusFirstValueCell, gridData.length, gridHeaders.length]);

  /** 入力形式を長形式⇔2列で切り替えたとき、長形式表で決めた列幅を引き継ぐ */
  useEffect(() => {
    if (linkedDataColumnWidthPx == null) return;
    const next = clampKeisukeDashboardDataColWidthPx(linkedDataColumnWidthPx);
    setTTestDataColWidthPx((prev) => (prev === next ? prev : next));
  }, [linkedDataColumnWidthPx]);

  useEffect(() => {
    const el = formWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setViewportInnerW((prev) => (Math.abs(prev - w) < 0.5 ? prev : w));
    });
    ro.observe(el);
    setViewportInnerW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      tTestColDragRef.current = null;
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
  }, []);

  /** 空は親コンポジション幅内で充填（全幅砂漠は composition が防ぐ） */
  const fillColWidth = keisukeDashboardFillDataColWidthPx(viewportInnerW, 2);
  const displayDataColWidthPx = userResizedColWidth ? tTestDataColWidthPx : fillColWidth;

  const commitFormToGrid = useCallback(
    (f: TTestTwoColumnForm) => {
      const paired = testType === 'paired';
      const longKeys = resolveUnpairedLongFormatColumnKeys(t, {
        groupKey: groupColumn,
        valueKey: valueColumn,
        valueAxisLabel,
      });
      const { headers, data } = paired
        ? tTestTwoColumnFormToGridDataPaired(f)
        : tTestTwoColumnFormToGridData(f, longKeys);
      suppressGridSyncUntilRef.current = Date.now() + TTEST_GRID_SYNC_SUPPRESS_MS;
      skipSyncFromGridRef.current = true;
      onApply(data, headers, { paired });
    },
    [testType, onApply, t, groupColumn, valueColumn, valueAxisLabel]
  );

  const applyForm = useCallback(() => commitFormToGrid(form), [commitFormToGrid, form]);
  const applyFormRef = useRef(applyForm);
  applyFormRef.current = applyForm;
  /** form 変化のみ監視（applyForm 同一性で二重スケジュールしない） */
  useDebouncedFormApply(() => applyFormRef.current(), [form], {
    delayMs: 280,
    skipInitial: true,
  });

  /**
   * サンプル remount 直後: init で Before→前 等に寄せたラベルをストア／マッピングへも反映する。
   */
  const didMountLocalizeApplyRef = useRef(false);
  useLayoutEffect(() => {
    if (didMountLocalizeApplyRef.current) return;
    didMountLocalizeApplyRef.current = true;
    const hasVals = form.valueRows.some(
      ([a, b]) => String(a).trim() !== '' || String(b).trim() !== ''
    );
    if (!hasVals || testType !== 'paired') return;
    const gridA = beforeColumn ?? gridHeaders[0];
    const gridB = afterColumn ?? gridHeaders[1];
    if (gridA && gridB && (form.labelColA !== gridA || form.labelColB !== gridB)) {
      commitFormToGrid(form);
    }
    // mount 時のみ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTTestSpreadsheetPaste = useCallback(
    (e: React.ClipboardEvent, focusCell: TTestGridCell) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      e.stopPropagation();
      const matrix = parseClipboardTsvMatrix(text);
      const anchor = getTTestPasteAnchorCell(gridSelection, focusCell);
      setForm((prev) => {
        let next = applyTTestMatrixPaste(prev, matrix, anchor, nPerGroup);
        if (next.valueRows.length > KEISUKE_GROUP_OBSERVATION_N_MAX) {
          next = {
            ...next,
            valueRows: next.valueRows.slice(0, KEISUKE_GROUP_OBSERVATION_N_MAX),
          };
        }
        if (next.valueRows.length > nPerGroup) {
          queueMicrotask(() => onNPerGroupChange(next.valueRows.length));
        }
        queueMicrotask(() => commitFormToGrid(next));
        return next;
      });
    },
    [disabled, gridSelection, nPerGroup, onNPerGroupChange, commitFormToGrid]
  );

  /** capture で処理し、子 input の paste と競合しないようにする */
  const onTablePasteCapture = useCallback(
    (e: React.ClipboardEvent<HTMLTableElement>) => {
      if (disabled) return;
      const inp = tTestInputFromClipboardEvent(e, tableRef.current);
      if (!inp) return;
      const cell = tTestCellFromEventTarget(inp);
      if (!cell) return;
      applyTTestSpreadsheetPaste(e, cell);
    },
    [applyTTestSpreadsheetPaste, disabled]
  );

  const setLabelA = (v: string) => setForm((prev) => ({ ...prev, labelColA: v }));
  const setLabelB = (v: string) => setForm((prev) => ({ ...prev, labelColB: v }));
  const setValueRow = (rowIndex: number, colIndex: 0 | 1, value: string) => {
    const v = normalizeKeisukeNumericInput(value);
    setForm((prev) => {
      const rows: [string, string][] = prev.valueRows.map((row, i) =>
        i === rowIndex
          ? [colIndex === 0 ? v : row[0], colIndex === 1 ? v : row[1]]
          : [row[0], row[1]]
      );
      return { ...prev, valueRows: rows };
    });
  };
  useEffect(() => {
    if (!isDragSelecting) return;
    const onMove = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = tTestCellFromEventTarget(el);
      if (cell && gridAnchorRef.current) {
        setGridSelection({ a: gridAnchorRef.current, b: cell });
      }
    };
    const onUp = () => setIsDragSelecting(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [isDragSelecting]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current?.contains(t)) return;
      setGridSelection(null);
      gridAnchorRef.current = null;
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, []);

  const onTablePointerDownCapture = (e: React.PointerEvent<HTMLTableElement>) => {
    if (disabled || e.button !== 0) return;
    const cell = tTestCellFromEventTarget(e.target);
    if (!cell) return;
    if (e.shiftKey && gridAnchorRef.current) {
      setGridSelection({ a: gridAnchorRef.current, b: cell });
      return;
    }
    gridAnchorRef.current = cell;
    setGridSelection({ a: cell, b: cell });
    setIsDragSelecting(true);
  };

  const onTableCopyCapture = (e: React.ClipboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    const inp = tTestInputFromClipboardEvent(e, tableRef.current);
    if (!inp) return;
    const resolved = resolveTTestClipboardSelection(gridSelection, inp);
    if (!resolved || resolved.deferToNative) return;
    e.preventDefault();
    e.stopPropagation();
    e.clipboardData?.setData('text/plain', buildTsvFromTTestSelection(form, resolved.norm));
  };

  const onTableCutCapture = (e: React.ClipboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    const inp = tTestInputFromClipboardEvent(e, tableRef.current);
    if (!inp) return;
    const resolved = resolveTTestClipboardSelection(gridSelection, inp);
    if (!resolved || resolved.deferToNative) return;
    e.preventDefault();
    e.stopPropagation();
    e.clipboardData?.setData('text/plain', buildTsvFromTTestSelection(form, resolved.norm));
    setForm((prev) => {
      const next = clearTTestSelectionCells(prev, resolved.norm);
      queueMicrotask(() => commitFormToGrid(next));
      return next;
    });
  };

  const focusTTestCell = (row: number, col: 0 | 1) => {
    const next = formWrapRef.current?.querySelector<HTMLInputElement>(
      `input[data-tt-row="${row}"][data-tt-col="${col}"]`
    );
    next?.focus();
    next?.select();
  };

  /** キーボード移動時は範囲選択をフォーカスセルへ畳む（離れた旧選択への Delete 事故防止） */
  const moveTTestFocus = (row: number, col: 0 | 1) => {
    const cell = { row, col } as TTestGridCell;
    gridAnchorRef.current = cell;
    setGridSelection({ a: cell, b: cell });
    focusTTestCell(row, col);
  };

  const onTableKeyDownCapture = (e: React.KeyboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      if (!gridSelection) return;
      e.preventDefault();
      setGridSelection(null);
      gridAnchorRef.current = null;
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (!gridSelection) return;
      const norm = normalizeTTestGridSelection(gridSelection);
      if (tTestGridSelectionCellCount(norm) <= 1) return;
      const focusCell = tTestCellFromEventTarget(e.target);
      if (!focusCell || !tTestCellInSelection(focusCell.row, focusCell.col, gridSelection)) {
        return;
      }
      e.preventDefault();
      setForm((prev) => {
        const next = clearTTestSelectionCells(prev, norm);
        queueMicrotask(() => commitFormToGrid(next));
        return next;
      });
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      const last = Math.max(0, form.valueRows.length - 1);
      const a: TTestGridCell = { row: -1, col: 0 };
      const b: TTestGridCell = { row: last, col: 1 };
      gridAnchorRef.current = a;
      setGridSelection({ a, b });
      return;
    }

    const cell = tTestCellFromEventTarget(e.target);
    if (!cell) return;
    const rowCount = form.valueRows.length;
    const last = rowCount - 1;

    /** Ctrl/Cmd+Home → 先頭データ、Ctrl/Cmd+End → 末尾（編集中のキャレット移動と衝突させない） */
    if ((e.ctrlKey || e.metaKey) && (e.key === 'Home' || e.key === 'End')) {
      e.preventDefault();
      if (e.key === 'Home') moveTTestFocus(0, 0);
      else moveTTestFocus(Math.max(0, last), 1);
      return;
    }

    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      const inp = e.target as HTMLInputElement;
      if (!tTestArrowShouldLeaveCell(e.key, inp)) return;
      e.preventDefault();
      const { row, col } = cell;
      if (e.key === 'ArrowUp') {
        if (row < 0) return;
        moveTTestFocus(row === 0 ? -1 : row - 1, col);
        return;
      }
      if (e.key === 'ArrowDown') {
        if (row < 0) {
          moveTTestFocus(0, col);
          return;
        }
        if (row < last) moveTTestFocus(row + 1, col);
        return;
      }
      if (e.key === 'ArrowLeft') {
        if (col === 1) {
          moveTTestFocus(row, 0);
          return;
        }
        if (row > 0) moveTTestFocus(row - 1, 1);
        else if (row === 0) moveTTestFocus(-1, 1);
        return;
      }
      // ArrowRight
      if (col === 0) {
        moveTTestFocus(row, 1);
        return;
      }
      if (row < 0) moveTTestFocus(0, 0);
      else if (row < last) moveTTestFocus(row + 1, 0);
      return;
    }

    /** Enter / Shift+Enter で縦移動（ヘッダ↔1行目、最終行 Enter→行追記） */
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (cell.row < 0) {
      moveTTestFocus(0, cell.col);
      return;
    }
    if (e.shiftKey) {
      moveTTestFocus(cell.row > 0 ? cell.row - 1 : -1, cell.col);
      return;
    }
    if (cell.row < last) {
      moveTTestFocus(cell.row + 1, cell.col);
      return;
    }
    /** valueRows 長を正とする（nPerGroup と一時的にずれても切らない） */
    const nextN = Math.min(rowCount + 1, KEISUKE_GROUP_OBSERVATION_N_MAX);
    if (nextN <= rowCount) return;
    const newRow = rowCount;
    prevNPerGroupRef.current = nextN;
    setForm((prev) => ({
      ...prev,
      valueRows: resizeTTestValueRows(prev.valueRows, nextN),
    }));
    onNPerGroupChange(nextN);
    queueMicrotask(() => moveTTestFocus(newRow, cell.col));
  };

  const gridCellClass = (row: number, col: 0 | 1) =>
    tTestCellInSelection(row, col, gridSelection) ? grid.tTestTwoColCellSelected : undefined;

  const clearTTestColResizeChrome = () => {
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
  };

  const onTTestColResizePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    /** 充填幅表示中に userResized だけ立てると初期 120px へ一瞬スナップするのを防ぐ */
    const startW = displayDataColWidthPx;
    setTTestDataColWidthPx(startW);
    setUserResizedColWidth(true);
    tTestColDragRef.current = {
      startX: e.clientX,
      startW,
    };
  };

  const onTTestColResizePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = tTestColDragRef.current;
    if (!sess) return;
    const dx = e.clientX - sess.startX;
    const nextW = clampKeisukeDashboardDataColWidthPx(sess.startW + dx);
    setTTestDataColWidthPx(nextW);
  };

  const onTTestColResizePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = tTestColDragRef.current;
    if (sess) {
      const finalW = clampKeisukeDashboardDataColWidthPx(e.clientX - sess.startX + sess.startW);
      onLinkedDataColumnWidthCommit?.(finalW);
    }
    tTestColDragRef.current = null;
    clearTTestColResizeChrome();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const onTTestColResizeLostCapture = () => {
    tTestColDragRef.current = null;
    clearTTestColResizeChrome();
  };

  return (
    <div
      ref={formWrapRef}
      className={`${styles.formWrap} ${styles.formWrapDense}`}
      data-keisuke-compact-form
    >
      <div className={styles.ttestControls}>
        <div className={styles.ttestModeRow}>
          <KeisukeLabeledBinaryRadioGroup
            label={t('keisuke.dashboard.ttest.testType')}
            className={styles.ttestModeCluster}
            labelClassName={styles.ttestModeClusterLabel}
            groupClassName={styles.ttestModeGroup}
            pillClassName={styles.ttestModePill}
            pillActiveClassName={styles.ttestModePillActive}
            first={{
              active: !isPaired,
              disabled,
              onSelect: () => setTestType('unpaired'),
              children: t('keisuke.dashboard.ttest.unpaired'),
            }}
            second={{
              active: isPaired,
              disabled,
              onSelect: () => setTestType('paired'),
              children: t('keisuke.dashboard.ttest.paired'),
            }}
          />
          <KeisukeLabeledBinaryRadioGroup
            label={t('keisuke.dashboard.ttest.inferential')}
            className={styles.ttestModeCluster}
            labelClassName={styles.ttestModeClusterLabel}
            groupClassName={styles.ttestModeGroup}
            pillClassName={styles.ttestModePill}
            pillActiveClassName={styles.ttestModePillActive}
            first={{
              active: isParametric,
              disabled,
              onSelect: () => setInferential('parametric'),
              children: tTestParametricInferenceLabel(t),
            }}
            second={{
              active: !isParametric,
              disabled,
              onSelect: () => setInferential('nonparametric'),
              children: isPaired
                ? t('keisuke.dashboard.ttest.wilcoxon')
                : t('keisuke.dashboard.ttest.mannWhitney'),
            }}
          />
          {isParametric && !isPaired ? (
            <KeisukeLabeledBinaryRadioGroup
              label={t('keisuke.dashboard.ttest.varianceMethod')}
              className={styles.ttestModeCluster}
              labelClassName={styles.ttestModeClusterLabel}
              groupClassName={styles.ttestModeGroup}
              pillClassName={styles.ttestModePill}
              pillActiveClassName={styles.ttestModePillActive}
              first={{
                active: !equalVariance,
                disabled,
                title: t('keisuke.dashboard.ttest.welchHint'),
                onSelect: () => setEqualVariance(false),
                children: (
                  <>
                    {t('keisuke.dashboard.ttest.welch')}
                    <span className={styles.ttestModeBadge} aria-hidden>
                      {t('keisuke.dashboard.ttest.recommendedShort')}
                    </span>
                  </>
                ),
              }}
              second={{
                active: equalVariance,
                disabled,
                onSelect: () => setEqualVariance(true),
                children: t('keisuke.dashboard.ttest.student'),
              }}
            />
          ) : null}
        </div>
        {isParametric && !isPaired && equalVariance ? (
          <p className={styles.ttestModeHint}>
            {t('keisuke.dashboard.ttest.studentHint')}
          </p>
        ) : null}
      </div>
      <KeisukeDashboardGridObservationCountRow
        label={
          isPaired
            ? t('keisuke.dashboard.inputForm.nPairs')
            : t('keisuke.dashboard.inputForm.nPerGroupTTest')
        }
        value={nPerGroup}
        onChange={onNPerGroupChange}
        onBlur={applyForm}
        disabled={disabled}
        max={KEISUKE_GROUP_OBSERVATION_N_MAX}
      />
      <div className={grid.keisukeAnovaTableWrap} data-keisuke-table-wrap>
        <div className={grid.tableScroll} data-keisuke-table-scroll>
        <table
          ref={tableRef}
          className={`${grid.keisukeAnovaGridTable} ${grid.keisukeAnovaGridTableAlignCenter}`}
          onPointerDownCapture={onTablePointerDownCapture}
          onCopyCapture={onTableCopyCapture}
          onCutCapture={onTableCutCapture}
          onPasteCapture={onTablePasteCapture}
          onKeyDownCapture={onTableKeyDownCapture}
        >
          <colgroup>
            <col className={grid.keisukeRowNumCol} />
            <col
              className={grid.keisukeAnovaColData}
              style={{ width: `${displayDataColWidthPx}px` }}
            />
            <col
              className={grid.keisukeAnovaColData}
              style={{ width: `${displayDataColWidthPx}px` }}
            />
          </colgroup>
          <thead>
            <tr>
              <th className={grid.keisukeRowNumCell} aria-hidden>#</th>
              <th
                className={[grid.keisukeTTestColResizeHost, gridCellClass(-1, 0)].filter(Boolean).join(' ') || undefined}
              >
                <input
                  type="text"
                  className={grid.keisukeAnovaGridInputHeader}
                  id={`${fieldIdPrefix}-tt-h-0`}
                  name={`${fieldIdPrefix}-tt-h-0`}
                  data-tt-row={-1}
                  data-tt-col={0}
                  value={form.labelColA}
                  onChange={(e) => setLabelA(e.target.value)}
                  onBlur={applyForm}
                  placeholder={defaultLabelA}
                  disabled={disabled}
                  aria-label={labelAriaA}
                />
                <button
                  type="button"
                  className={grid.keisukeTTestColResizeHandle}
                  data-tt-ignore-selection="1"
                  tabIndex={-1}
                  disabled={disabled}
                  aria-label={resizeColumnsAria}
                  onPointerDown={onTTestColResizePointerDown}
                  onPointerMove={onTTestColResizePointerMove}
                  onPointerUp={onTTestColResizePointerUp}
                  onPointerCancel={onTTestColResizePointerUp}
                  onLostPointerCapture={onTTestColResizeLostCapture}
                />
              </th>
              <th className={gridCellClass(-1, 1)}>
                <input
                  type="text"
                  className={grid.keisukeAnovaGridInputHeader}
                  id={`${fieldIdPrefix}-tt-h-1`}
                  name={`${fieldIdPrefix}-tt-h-1`}
                  data-tt-row={-1}
                  data-tt-col={1}
                  value={form.labelColB}
                  onChange={(e) => setLabelB(e.target.value)}
                  onBlur={applyForm}
                  placeholder={defaultLabelB}
                  disabled={disabled}
                  aria-label={labelAriaB}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {form.valueRows.map((row, i) => (
              <tr key={i}>
                <td className={grid.keisukeRowNumCell} aria-hidden>{i + 1}</td>
                <td className={gridCellClass(i, 0)}>
                  <input
                    type="text"
                    className={grid.keisukeAnovaGridInputCell}
                    id={`${fieldIdPrefix}-tt-r${i}-c0`}
                    name={`${fieldIdPrefix}-tt-r${i}-c0`}
                    data-tt-row={i}
                    data-tt-col={0}
                    value={row[0]}
                    onChange={(e) => setValueRow(i, 0, e.target.value)}
                    onBlur={applyForm}
                    inputMode="decimal"
                    disabled={disabled}
                    aria-label={t('keisuke.dashboard.inputForm.valueRow', {
                      row: i + 1,
                      col: 1,
                    })}
                  />
                </td>
                <td className={gridCellClass(i, 1)}>
                  <input
                    type="text"
                    className={grid.keisukeAnovaGridInputCell}
                    id={`${fieldIdPrefix}-tt-r${i}-c1`}
                    name={`${fieldIdPrefix}-tt-r${i}-c1`}
                    data-tt-row={i}
                    data-tt-col={1}
                    value={row[1]}
                    onChange={(e) => setValueRow(i, 1, e.target.value)}
                    onBlur={applyForm}
                    inputMode="decimal"
                    disabled={disabled}
                    aria-label={t('keisuke.dashboard.inputForm.valueRow', {
                      row: i + 1,
                      col: 2,
                    })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <KeisukeClipboardHintBar />
    </div>
  );
}
